import { z } from 'zod';
// ============================================================
// ENUMS / LITERALS
// ============================================================
export const reportTypeSchema = z.enum([
    'income_statement', 'balance_sheet', 'trial_balance',
    'cash_flow', 'budget_vs_actual', 'custom',
]);
export const parameterTypeSchema = z.enum([
    'company', 'period', 'date', 'dropdown', 'boolean',
]);
export const columnTypeSchema = z.enum([
    'label', 'current', 'prior_period', 'prior_year',
    'budget', 'ytd', 'variance_amount', 'variance_percent', 'calculated',
    'period_offset', 'year_offset',
]);
export const accountTypeSchema = z.enum([
    'asset', 'liability', 'equity', 'revenue', 'expense',
]);
export const rowStyleSchema = z.enum([
    'normal', 'bold', 'underline', 'double_underline',
]);
// ============================================================
// PARAMETERS
// ============================================================
export const parameterDefinitionSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: parameterTypeSchema,
    required: z.boolean(),
    defaultValue: z.string().optional(),
});
// ============================================================
// COLUMNS
// ============================================================
export const columnFormatSchema = z.object({
    type: z.enum(['currency', 'percent', 'number', 'text']),
    decimals: z.number().int().min(0).max(10).optional(),
    negativeFormat: z.enum(['minus', 'parentheses', 'red']).optional(),
});
export const columnDefinitionSchema = z.object({
    id: z.string().min(1),
    header: z.string().min(1),
    type: columnTypeSchema,
    width: z.number().positive().optional(),
    format: columnFormatSchema.optional(),
    formula: z.string().optional(),
    periodOffset: z.number().int().optional(),
    budgetName: z.string().optional(),
    baseColumnId: z.string().optional(),
    compareColumnId: z.string().optional(),
});
// ============================================================
// ROWS (Discriminated Union)
// ============================================================
export const headerRowSchema = z.object({
    type: z.literal('header'),
    id: z.string().min(1),
    text: z.string().min(1),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
});
export const accountFilterSchema = z.object({
    types: z.array(accountTypeSchema).optional(),
    parentCode: z.string().optional(),
    codes: z.array(z.string()).optional(),
    excludeCodes: z.array(z.string()).optional(),
});
export const sectionRowSchema = z.object({
    type: z.literal('section'),
    id: z.string().min(1),
    name: z.string().min(1),
    filter: accountFilterSchema,
    showSubtotal: z.boolean(),
    subtotalLabel: z.string().optional(),
});
export const accountRowSchema = z.object({
    type: z.literal('account'),
    id: z.string().min(1),
    accountCode: z.string().min(1),
    label: z.string().optional(),
});
export const subtotalRowSchema = z.object({
    type: z.literal('subtotal'),
    id: z.string().min(1),
    label: z.string().min(1),
    sumOf: z.array(z.string()),
    style: rowStyleSchema.optional(),
});
export const calculatedRowSchema = z.object({
    type: z.literal('calculated'),
    id: z.string().min(1),
    label: z.string().min(1),
    formula: z.string().min(1),
    style: rowStyleSchema.optional(),
});
export const spacerRowSchema = z.object({
    type: z.literal('spacer'),
    id: z.string().min(1),
});
export const grandTotalRowSchema = z.object({
    type: z.literal('grand_total'),
    id: z.string().min(1),
    label: z.string().min(1),
    sumOf: z.array(z.string()),
});
export const lineRowSchema = z.object({
    type: z.literal('line'),
    id: z.string().min(1),
    borderStyle: z.enum(['thin', 'medium', 'thick', 'double']),
});
export const titleBlockRowSchema = z.object({
    type: z.literal('title_block'),
    id: z.string().min(1),
    text: z.string(),
    alignment: z.enum(['left', 'center', 'right']),
    fontSize: z.number().int().min(6).max(48).optional(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
});
export const columnHeadersRowSchema = z.object({
    type: z.literal('column_headers'),
    id: z.string().min(1),
    label: z.string().optional(),
    columns: z.array(columnDefinitionSchema).min(1),
});
export const rowDefinitionSchema = z.union([
    headerRowSchema,
    sectionRowSchema,
    accountRowSchema,
    subtotalRowSchema,
    calculatedRowSchema,
    spacerRowSchema,
    grandTotalRowSchema,
    lineRowSchema,
    titleBlockRowSchema,
    columnHeadersRowSchema,
]);
// ============================================================
// ROW NODE (Tree Structure)
// ============================================================
export const rowNodeSchema = z.lazy(() => z.object({
    id: z.string().min(1),
    rowId: z.string().min(1),
    depth: z.number().int().min(0),
    columnHeadersId: z.string().optional(),
    children: z.array(rowNodeSchema),
    data: rowDefinitionSchema,
}));
// ============================================================
// LEVEL COLUMN CONFIG
// ============================================================
export const levelColumnConfigSchema = z.object({
    default: z.array(columnDefinitionSchema),
    levels: z.record(z.coerce.number(), z.array(columnDefinitionSchema)),
});
// ============================================================
// EXCEL LAYOUT SETTINGS
// ============================================================
export const excelLayoutSettingsSchema = z.object({
    labelIndent: z.enum(['space', 'column']),
    amountPosition: z.enum(['adjacent', 'fixed']),
    fixedAmountColumn: z.string().max(2).optional(),
    spacesPerLevel: z.number().int().min(1).max(10),
    showGridlines: z.boolean(),
    freezeHeaderRow: z.boolean(),
    repeatHeaderOnPrint: z.boolean(),
});
// ============================================================
// THEME
// ============================================================
export const themeSettingsSchema = z.object({
    colors: z.object({
        headerBackground: z.string(),
        sectionBackground: z.string(),
        subtotalBackground: z.string(),
    }),
    negativeFormat: z.enum(['red', 'parentheses', 'both']),
});
// ============================================================
// REPORT DEFINITION (Top-Level)
// ============================================================
export const reportDefinitionSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: reportTypeSchema,
    version: z.number().int().positive(),
    parameters: z.array(parameterDefinitionSchema),
    columns: z.array(columnDefinitionSchema),
    rows: z.array(rowDefinitionSchema),
    theme: themeSettingsSchema,
    metadata: z.object({
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
    }),
});
// ============================================================
// REPORT DEFINITION V2 (Tree-Based)
// ============================================================
export const reportDefinitionV2Schema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: reportTypeSchema,
    version: z.number().int().positive(),
    parameters: z.array(parameterDefinitionSchema),
    columns: z.array(columnDefinitionSchema),
    rows: z.array(rowDefinitionSchema),
    nodes: z.array(rowNodeSchema),
    columnConfig: levelColumnConfigSchema,
    layoutSettings: excelLayoutSettingsSchema,
    theme: themeSettingsSchema,
    metadata: z.object({
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
    }),
});
//# sourceMappingURL=validation.js.map